/**
 * Addon and Cart Management Script
 *
 * Handles:
 * - Automatic addon synchronization (rush delivery, logo types, FLITE upgrades)
 * - Cart property management for product customizations
 * - Team order functionality with localStorage integration
 * - Goalkeeper jersey addon management
 *
 * Configuration:
 * - addonTypes: Maps product properties to variant IDs and quantities
 * - isSyncing: Prevents race conditions during cart updates
 *
 * Events:
 * - DOMContentLoaded: Initializes cart sync and event handlers
 * - click: Listens for add-to-cart and remove-from-cart buttons
 * - cart:updated: Custom event for cart changes
 *
 * Dependencies:
 * - Shopify Cart API (/cart.json, /cart/add.js, /cart/change.js)
 * - localStorage for team order data
 *
 * @author Lavi Pun Studio Theme Team
 * @version 2.0
 */

// ── HTML escaping (XSS hardening) ──────────────────────────────────────────
// team_name / team_logo / url / coach_name[] / age_group[] come from localStorage
// (attacker-writable) and are interpolated into insertAdjacentHTML below. Escape
// every one. URL-ish attributes additionally reject javascript:-scheme values.
// Local copy on purpose: this file is edited directly and is NOT in the FPD
// obfuscation pipeline, so it cannot import the interactions helper.
function addonEscapeHtml(str) {
  if (str === null || str === undefined) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
function addonEscapeUrlAttr(url, fallback) {
  if (fallback === undefined) fallback = "";
  if (url === null || url === undefined) return fallback;
  const raw = String(url).trim();
  const scheme = raw.replace(/[\s]/g, "").toLowerCase();
  if (/^(javascript|vbscript):/.test(scheme) || /^data:text\/html/.test(scheme)) return fallback;
  return addonEscapeHtml(raw);
}

// document.addEventListener("DOMContentLoaded", () => {
//   console.log("Theme script loaded!");

//   // Dynamic Addon Configuration
//   const addonTypes = {
//     logo_type_woven: {
//       variantId: 45784052465716,
//       quantityType: "same_quantity", // Single item addon
//       propertyCheck: { "LOGO TYPE": "Woven Patch" }, // Match property for this addon
//     },
//     logo_type_rubber: {
//       variantId: 45698351759412,
//       quantityType: "single", // Single item addon
//       propertyCheck: { "LOGO TYPE": "3D Rubber" }, // Match property for this addon
//     },
//     rush_delivery: {
//       variantId: 44789369176116,
//       quantityType: "same_quantity", // Single item addon
//       propertyCheck: { "RUSH DELIVERY": "Yes" }, // Match property for this addon
//     },
//   };

//   const syncCartWithAddons = async () => {
//     let addonsModified = false; // Track if changes were made to the cart

//     try {
//       console.log("Fetching cart...");
//       const cart = await fetch("/cart.json").then((res) => res.json());
//       console.log("Cart data:", cart);

//       if (!cart.items || cart.items.length === 0) {
//         console.log("Cart is empty. No addons to process.");
//         return;
//       }

//       const lineItems = cart.items;
//       const parentProductMap = {};
//       const addonsInCart = {};

//       // Parse current cart items
//       lineItems.forEach((item) => {
//         if (item.properties && item.properties.addon_type) {
//           addonsInCart[item.id] = item.properties.parent_id; // Track existing addons
//         } else {
//           parentProductMap[item.id] = item; // Regular product
//         }
//       });

//       // Handle addon logic for each parent product
//       for (const [parentId, product] of Object.entries(parentProductMap)) {
//         for (const [addonType, addonConfig] of Object.entries(addonTypes)) {
//           const { variantId, quantityType, propertyCheck } = addonConfig;

//           // Check if product matches addon conditions
//           const hasAddonCondition = Object.entries(propertyCheck).some(
//             ([key, value]) =>
//               product.properties &&
//               product.properties[key] &&
//               product.properties[key].startsWith(value)
//           );

//           const addonExists = Object.values(addonsInCart).includes(parentId);

//           if (hasAddonCondition && !addonExists) {
//             const addonQuantity =
//               quantityType === "same_quantity" ? product.quantity : 1;

//             console.log(
//               `Adding addon: ${addonType} (Variant ID: ${variantId})`
//             );

//             // Add the addon product
//             await fetch("/cart/add.js", {
//               method: "POST",
//               headers: { "Content-Type": "application/json" },
//               body: JSON.stringify({
//                 id: variantId,
//                 quantity: addonQuantity,
//                 properties: { parent_id: parentId, addon_type: addonType },
//               }),
//             });

//             addonsModified = true; // Mark that the cart was modified
//           }
//         }
//       }

//       // Remove orphan addons (if parent product is removed)
//       for (const [addonId, parentId] of Object.entries(addonsInCart)) {
//         if (!parentProductMap[parentId]) {
//           console.log(`Removing orphan addon: ${addonId}`);
//           await fetch(`/cart/change.js`, {
//             method: "POST",
//             headers: { "Content-Type": "application/json" },
//             body: JSON.stringify({ id: addonId, quantity: 0 }),
//           });

//           addonsModified = true; // Mark that the cart was modified
//         }
//       }
//     } catch (error) {
//       console.error("Error syncing cart addons:", error);
//     }

//     // Reload the page if changes were made
//     if (addonsModified) {
//       console.log("Cart modified. Reloading page...");
//       window.location.reload(); // Reload the page to reflect changes
//     }
//   };

//   const attachCartEventHandlers = () => {
//     console.log("Attaching cart event handlers...");

//     document.addEventListener("click", async (event) => {
//       if (event.target.matches(".add-to-cart, .remove-from-cart")) {
//         console.log("Cart button clicked");
//         await syncCartWithAddons();
//       }
//     });

//     // Handle cart updates (for cart drawers or popups)
//     document.addEventListener("cart:updated", async () => {
//       console.log("Cart updated event triggered");
//       await syncCartWithAddons();
//     });
//   };

//   // Initialize
//   console.log("Initializing cart sync...");
//   syncCartWithAddons();
//   
// });

// Debug-gated logger: routine sync chatter stays out of the production
// console (enable with localStorage.lexaAddonDebug = "1" or ?addon_debug).
// Warnings and errors always log.
const ADDON_DEBUG = (() => {
  try {
    return /[?&]addon_debug/.test(window.location.search) ||
      window.localStorage.getItem("lexaAddonDebug") === "1";
  } catch (e) { return false; }
})();
function addonLog() {
  if (!ADDON_DEBUG) return;
  try { console.log.apply(console, arguments); } catch (e) { /* never throw */ }
}

document.addEventListener("DOMContentLoaded", () => {
  addonLog("Theme script loaded!");

  const addonTypes = {
    logo_type_woven: {
      variantId: 45784052465716, // Variant ID of the addon
      quantityType: "same_quantity", // Matches parent product quantity
      propertyCheck: { "LOGO TYPE": "Woven Patch" }, // Property to match
    },
    logo_type_rubber: {
      variantId: 45698351759412, // Variant ID of the addon
      quantityType: "single", // Single item addon
      propertyCheck: { "LOGO TYPE": "3D Rubber" }, // Property to match
    },
    rush_delivery: {
      variantId: 44789369176116, // Variant ID of the addon
      quantityType: "same_quantity", // Matches parent product quantity
      propertyCheck: { "Delivery Type": "Rush" }, // Property to match (startsWith "Rush")
    },
    tackle_twill_embroidery: {
      variantId: 45963695063092, // Variant ID of the addon
      quantityType: "same_quantity", // Matches parent product quantity
      propertyCheck: { "LOGO TYPE": "Tackle Twill Embroidery" }, // Property to match
    },
    full_kit_upgrade: {
      variantId: 48044953108532, // Full kit upgrade addon ($20 shorts+socks) — "Full Kit Upgrade" product
      quantityType: "same_quantity", // Matches parent product quantity
      propertyCheck: { "Full Kit Upgrade": "Yes" }, // Property to match
    },
  };

  // Circuit breaker: stop retrying an addon variant that keeps 422ing (deleted /
  // unavailable variant). Without this the same failing POST fires on every cart
  // event forever and the failure only ever shows as console noise.
  const ADDON_FAIL_LIMIT = 3;
  const addonFailCounts = {};
  const addonDisabled = (type) =>
    (addonFailCounts[type] || 0) >= ADDON_FAIL_LIMIT ||
    sessionStorage.getItem("addonDisabled:" + type) === "1";
  const recordAddonFailure = (type) => {
    addonFailCounts[type] = (addonFailCounts[type] || 0) + 1;
    if (addonFailCounts[type] >= ADDON_FAIL_LIMIT) {
      sessionStorage.setItem("addonDisabled:" + type, "1");
      console.error(
        `Addon "${type}" failed ${ADDON_FAIL_LIMIT} times (variant missing/unavailable?). ` +
          "Disabling further retries this session — the customer will NOT be charged for this addon. " +
          "Check the addon variant ID in addon-lavi.js against the store."
      );
    }
  };

  /** Log Shopify Cart API error body (422 usually includes `description`). */
  const logCartApiError = async (label, res) => {
    let detail = "";
    try {
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("application/json")) {
        const j = await res.json();
        detail =
          typeof j.description === "string"
            ? j.description
            : JSON.stringify(j);
      } else {
        detail = (await res.text()).slice(0, 500);
      }
    } catch {
      detail = "(could not read response body)";
    }
    console.error(`${label}: HTTP ${res.status}`, detail || "(empty)");
  };

  let isSyncing = false;

  const syncCartWithAddons = async () => {
    if (isSyncing) {
      addonLog("Cart sync already in progress. Skipping.");
      return;
    }

    isSyncing = true;
    let addonsModified = false;

    try {
      addonLog("Fetching cart...");
      const cart = await fetch("/cart.json").then((res) => res.json());
      addonLog("Cart data:", cart);

      if (!cart.items || cart.items.length === 0) {
        addonLog("Cart is empty. No addons to process.");
        isSyncing = false;
        return;
      }

      const lineItems = cart.items;
      const parentProductMap = {};
      const addonsInCart = {}; // key → { parent_key, addon_type, isLegacy }
      const staleAddonKeys = new Set(); // old-format addon keys to clean up

      // Parse cart items – detect both new (_addon_type) and legacy (addon_type) formats
      lineItems.forEach((item) => {
        const p = item.properties || {};
        const newType = p._addon_type;
        const oldType = p.addon_type;

        if (newType) {
          addonsInCart[item.key] = {
            parent_key: p._parent_id,
            addon_type: newType,
            quantity: item.quantity,
            isLegacy: false,
          };
        } else if (oldType) {
          // Legacy addon from before the _ prefix migration
          addonsInCart[item.key] = {
            parent_key: p.parent_id,
            addon_type: oldType,
            isLegacy: true,
          };
          staleAddonKeys.add(item.key);
        } else {
          parentProductMap[item.key] = item;
        }
      });

      // For each parent product + each applicable addon type: ensure exactly one
      // up-to-date addon exists, removing any legacy or duplicate copies.
      for (const [parentKey, product] of Object.entries(parentProductMap)) {
        for (const [addonType, addonConfig] of Object.entries(addonTypes)) {
          const { variantId, quantityType, propertyCheck } = addonConfig;

          const hasAddonCondition = Object.entries(propertyCheck).every(
            ([key, value]) => product.properties?.[key]?.startsWith(value)
          );

          if (!hasAddonCondition || !variantId) continue;

          // All addons of this type linked to this parent (any format)
          const matching = Object.entries(addonsInCart).filter(
            ([, addon]) => addon.parent_key === parentKey && addon.addon_type === addonType
          );
          const validMatches  = matching.filter(([key]) => !staleAddonKeys.has(key));
          const legacyMatches = matching.filter(([key]) =>  staleAddonKeys.has(key));

          // Determine which to remove: always remove legacy; remove extra valids beyond 1
          const toRemove = [
            ...legacyMatches,
            ...validMatches.slice(1), // keep at most one valid
          ];

          for (const [addonKey] of toRemove) {
            addonLog(`Removing stale/duplicate addon: ${addonKey} (${addonType})`);
            const r = await fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ id: addonKey, quantity: 0 }),
            });
            if (r.ok) addonsModified = true;
            else await logCartApiError(`Failed to remove addon ${addonKey}`, r);
          }

          const expectedQuantity = quantityType === "same_quantity" ? product.quantity : 1;

          // Add a fresh addon only if no valid (non-legacy) one remains
          if (validMatches.length === 0) {
            if (addonDisabled(addonType)) continue; // repeated failures — stop hammering the API
            addonLog(`Adding addon: ${addonType} for parent ${parentKey}`);
            const addRes = await fetch("/cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                id: variantId,
                quantity: expectedQuantity,
                properties: { _parent_id: parentKey, _addon_type: addonType },
              }),
            });
            if (addRes.ok) addonsModified = true;
            else {
              await logCartApiError(`Failed to add addon ${addonType}`, addRes);
              recordAddonFailure(addonType);
            }
          } else {
            // Reconcile quantity: the parent's quantity may have changed since the
            // addon was created (cart edits). Without this, rush/full-kit fees are
            // silently undercharged (or overcharged) after any quantity change.
            const [addonKey, addon] = validMatches[0];
            if (addon.quantity !== expectedQuantity) {
              addonLog(
                `Reconciling addon ${addonType} quantity ${addon.quantity} -> ${expectedQuantity}`
              );
              const chRes = await fetch("/cart/change.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: addonKey, quantity: expectedQuantity }),
              });
              if (chRes.ok) addonsModified = true;
              else await logCartApiError(`Failed to update addon ${addonType} quantity`, chRes);
            }
          }
        }
      }

      // Remove orphan addons (parent no longer in cart)
      for (const [addonKey, addon] of Object.entries(addonsInCart)) {
        if (parentProductMap[addon.parent_key]) continue; // parent present – ok
        addonLog(`Removing orphan addon: ${addonKey}`);
        const removeRes = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: addonKey, quantity: 0 }),
        });
        if (removeRes.ok) addonsModified = true;
        else await logCartApiError(`Failed to remove orphan addon ${addonKey}`, removeRes);
      }
    } catch (error) {
      console.error("Error syncing cart addons:", error);
    }

    if (addonsModified) {
      if (sessionStorage.getItem("addonSyncJustReloaded")) {
        sessionStorage.removeItem("addonSyncJustReloaded");
        console.warn("Addon sync: skipping reload to prevent loop (already reloaded once).");
      } else {
        addonLog("Cart modified. Reloading page...");
        sessionStorage.setItem("addonSyncJustReloaded", "1");
        window.location.reload();
      }
    } else {
      // No-op sync: clear the reload guard so the NEXT legitimate cart change
      // still reloads (otherwise the guard leaks and the UI goes stale once).
      sessionStorage.removeItem("addonSyncJustReloaded");
    }

    isSyncing = false;
  };

  // Bounded retry after an ATC click/submit. A single fixed 800ms timer dropped
  // paid addons on slow networks: the theme's AJAX add landed after 800ms, sync
  // saw the old cart and added nothing, and never retried. Here we snapshot the
  // cart item_count at click time and attempt at 800/2500/5000ms; we sync as soon
  // as the count changes (parent add landed), else sync once at the end as a
  // best-effort. Strictly bounded — no infinite loops.
  const scheduleAddonSync = async () => {
    let baselineCount = null;
    try {
      const cart = await fetch("/cart.js").then((r) => r.json());
      baselineCount = cart.item_count;
    } catch (e) {
      // Baseline unknown — we'll just sync at the first attempt (no retries).
      console.warn("Addon sync: could not read baseline cart count:", e);
    }

    const times = [800, 2500, 5000];
    let attempt = 0;

    const tryOnce = async () => {
      // If we couldn't read a baseline, sync once and stop (avoid a loop).
      if (baselineCount === null) { await syncCartWithAddons(); return; }

      let changed = false;
      try {
        const cart = await fetch("/cart.js").then((r) => r.json());
        changed = cart.item_count !== baselineCount;
      } catch (e) {
        changed = true; // can't tell — err toward syncing
      }

      if (changed) { await syncCartWithAddons(); return; } // add landed → sync, stop

      attempt++;
      if (attempt < times.length) {
        setTimeout(tryOnce, times[attempt] - times[attempt - 1]);
      } else {
        // Give up retrying, but sync once so a very late add still gets its addons.
        await syncCartWithAddons();
      }
    };

    setTimeout(tryOnce, times[0]);
  };

  const attachCartEventHandlers = () => {
    addonLog("Attaching cart event handlers...");

    // Catch all ATC-style button clicks (legacy .add-to-cart, theme's button--atc-main, #atc-btn)
    document.addEventListener("click", async (event) => {
      const btn = event.target.closest(".add-to-cart, .remove-from-cart, .button--atc-main, #atc-btn");
      if (btn) {
        addonLog("Cart button clicked:", btn.id || btn.className);
        scheduleAddonSync();
      }
    });

    // Catch product form submissions (covers all product templates)
    document.addEventListener("submit", async (event) => {
      const form = event.target.closest("form[action='/cart/add']");
      if (form) {
        addonLog("Product form submitted");
        scheduleAddonSync();
      }
    });

    // Theme cart:updated event (fired after AJAX cart changes)
    document.addEventListener("cart:updated", async () => {
      addonLog("Cart updated event triggered");
      await syncCartWithAddons();
    });
  };

  addonLog("Initializing cart sync...");
  syncCartWithAddons();
  attachCartEventHandlers();
});

/*team order*/ document.addEventListener("DOMContentLoaded", async () => {
  // Parse all saved teams from localStorage.
  // Supports both a single object ("selectedTeam") and a list ("teamsList").
  let allTeams = [];
  try {
    const raw = localStorage.getItem("teamsList");
    if (raw) {
      const parsed = JSON.parse(raw);
      allTeams = Array.isArray(parsed) ? parsed : [parsed];
    }
  } catch (e) { /* ignore */ }

  // Fall back to the legacy single-team key
  if (allTeams.length === 0) {
    try {
      const raw = localStorage.getItem("selectedTeam");
      if (raw) allTeams = [JSON.parse(raw)];
    } catch (e) { /* ignore */ }
  }

  const renderTeamFields = (container, teamData) => {
    if (!container || container.querySelector(".team-order-fields")) return;
    const hasCoachNames =
      Array.isArray(teamData.coach_name) && teamData.coach_name.length > 0;
    const hasAgeGroups =
      Array.isArray(teamData.age_group) && teamData.age_group.length > 0;

    const html = `
      <style>
        .team-order-fields {
          display: flex;
          flex-direction: row;
          gap: 20px;
          margin-bottom: 40px;
        }
        .cart-attribute__field select {
          font-family: inherit;
          font-weight: inherit;
          font-style: inherit;
          display: flex;
          align-items: center;
          position: relative;
          max-width: 350px;
          padding: 11px 40px 11px 15px;
          background-color: transparent;
          color: inherit;
          font-size: medium;
          text-align: start;
          text-transform: none;
          border-radius: 5px;
          border: 1.5px solid lightgray;
          width: 100%;
        }
      </style>
      <div class="team-order-fields" style="margin-top: 10px;">
        <div class="cart-attribute__field hidden">
          <label for="team-order">Team Name:</label>
          <input id="team-order" type="text" name="attributes[team-order]" value="${addonEscapeHtml(teamData.team_name)}" readonly>
        </div>

        <div class="cart-attribute__field">
          <a href="${addonEscapeUrlAttr(teamData.url, '#')}" target="_blank" rel="noopener noreferrer" style="text-decoration: underline;">
            <img src="${addonEscapeUrlAttr(teamData.team_logo)}" alt="Team Logo" style="max-width: 100px; margin-top: 5px;" />
          </a>
        </div>

        ${hasCoachNames ? `
        <div class="cart-attribute__field">
          <label>Coach Name</label><br>
          <select id="coach-name" name="attributes[Coach Name]" required>
            <option value="" disabled selected>Select Coach</option>
            ${teamData.coach_name.filter((n) => n).map((n) => `<option value="${addonEscapeHtml(n)}">${addonEscapeHtml(n)}</option>`).join("")}
          </select>
        </div>` : ""}

        ${hasAgeGroups ? `
        <div class="cart-attribute__field">
          <label>Age Group</label><br>
          <select id="age-group" name="attributes[Age Group]" required>
            <option value="" disabled selected>Select Age Group</option>
            ${teamData.age_group.filter((g) => g).map((g) => `<option value="${addonEscapeHtml(g)}">${addonEscapeHtml(g)}</option>`).join("")}
          </select>
        </div>` : ""}
      </div>
    `;

    container.insertAdjacentHTML("afterbegin", html);

    const coachSelect = container.querySelector("#coach-name");
    const ageGroupSelect = container.querySelector("#age-group");

    [coachSelect, ageGroupSelect].forEach((select) => {
      if (!select) return;
      select.addEventListener("invalid", () => {
        if (!select.value) select.setCustomValidity("Please select a valid option");
      });
      select.addEventListener("change", () => select.setCustomValidity(""));
    });

    const updateAttribute = async (attrName, value) => {
      try {
        const cart = await fetch("/cart.js").then((res) => res.json());
        const updatedAttributes = { ...cart.attributes, [attrName]: value };
        await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attributes: updatedAttributes }),
        });
        addonLog(`Updated attribute '${attrName}' to '${value}'`);
      } catch (err) {
        console.error(`Error updating cart attribute '${attrName}':`, err);
      }
    };

    if (coachSelect) coachSelect.addEventListener("change", (e) => updateAttribute("Coach Name", e.target.value));
    if (ageGroupSelect) ageGroupSelect.addEventListener("change", (e) => updateAttribute("Age Group", e.target.value));
  };

  try {
    const cart = await fetch("/cart.js").then((res) => res.json());

    // Find the first teamwear line item and get its Team Name property value
    const teamwearItem = cart.items.find(
      (item) => item.properties && item.properties["Team Name"]
    );
    const cartTeamName = teamwearItem ? (item => item.properties["Team Name"])(teamwearItem) : null;

    if (cartTeamName) {
      // Find a matching team in localStorage by team_name (case-insensitive)
      const matchedTeam = allTeams.find(
        (t) => t && t.team_name && t.team_name.trim().toLowerCase() === cartTeamName.trim().toLowerCase()
      );

      if (matchedTeam) {
        const container = document.querySelector(".checkout-subtotal-container__left");
        if (container) {
          renderTeamFields(container, matchedTeam);
        } else {
          console.warn("Container '.checkout-subtotal-container__left' not found.");
        }
      } else {
        console.warn(`Team "${cartTeamName}" not found in localStorage teams. Skipping team UI.`);
      }
    } else {
      // Remove cart attributes if Teamwear not present
      const attributesToRemove = ["team-order", "Coach Name", "Age Group"];
      const updatedAttributes = { ...cart.attributes };
      let hasChanges = false;

      attributesToRemove.forEach((attr) => {
        if (updatedAttributes[attr]) {
          updatedAttributes[attr] = "";
          hasChanges = true;
        }
      });

      if (hasChanges) {
        addonLog("Removing orphaned cart attributes:", attributesToRemove);

        await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attributes: updatedAttributes }),
        });

        document.dispatchEvent(new Event("cart:updated"));
      }
    }
  } catch (err) {
    console.error("Error processing teamwear cart logic:", err);
  }
});
